import { HttpErrorResponse, HttpEvent, HttpEventType, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { catchError, tap, throwError } from 'rxjs';
import { StandardApiResponse } from '../interfaces/standard-api-response.interface';
import { ErrorReportService } from '../services/error-report.service';

const STATUS_MESSAGES: Record<number, string> = {
  400: 'Bad request.',
  401: 'You do not have access to view this page.',
  403: 'You do not have permission to access this resource.',
  404: 'The requested resource was not found.',
  409: 'This record was changed by another request. Reload the latest version before continuing.',
  429: 'Too many requests. Please try again shortly.',
  500: 'Something went wrong. Please contact support if this continues.',
};

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const message = inject(NzMessageService);
  const modal = inject(NzModalService);
  const notification = inject(NzNotificationService);
  const router = inject(Router);
  const errorReport = inject(ErrorReportService);
  const isTicketRequest = req.url.includes('/support/tickets');
  const requestPayload = typeof FormData !== 'undefined' && req.body instanceof FormData ? undefined : req.body;

  const showError = (text: string, status: number | undefined, responseBody: unknown): void => {
    const reported =
      !isTicketRequest &&
      errorReport.notify({
        message: text,
        status,
        method: req.method,
        url: req.urlWithParams,
        requestPayload,
        responseBody,
      });

    if (!reported) {
      message.error(text);
    }
  };

  return next(req).pipe(
    tap((event: HttpEvent<unknown>) => {
      if (event.type !== HttpEventType.Response) {
        return;
      }

      const body = (event as HttpResponse<Partial<StandardApiResponse<unknown>>>).body;

      if (body?.success === false) {
        showError(body.message || 'The request could not be completed.', undefined, body);
      }
    }),
    catchError((error: HttpErrorResponse) => {
      const body = error.error as Partial<StandardApiResponse<unknown>> | null | undefined;
      const serverMessage = body?.message;
      const fallback = STATUS_MESSAGES[error.status] ?? 'An unexpected error occurred.';

      if (error.status === 401 || error.status === 403) {
        notification.warning(
          'Access denied',
          'You do not have access to view this page.',
        );

        if (error.status === 401) {
          router.navigate(['/auth/login']);
        }
      } else if (error.status === 409) {
        modal.warning({
          nzTitle: 'This record was updated elsewhere',
          nzContent: serverMessage || fallback,
          nzOkText: 'Reload latest version',
          nzOnOk: () => window.location.reload(),
        });
      } else {
        showError(serverMessage || fallback, error.status, error.error);
      }

      return throwError(() => error);
    })
  );
};
